import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import type { Me } from '@greco-gls/shared';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
import type { SvgIconComponent } from '@mui/icons-material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router';

import { helpAnchor } from '@/help/anchor';
import { AI_DISABLED_REASON, AI_ENABLED } from '@/lib/features';
import { HelpLauncher } from '@/help/HelpLauncher';
import {
  attentionId,
  isAttentionUnread,
  pruneAttentionReadMarks,
  useAttention,
  type AttentionItem,
} from '@/hooks';
import { MOTION } from '@/theme';

import { GlobalKeySearch } from './GlobalKeySearch';
import {
  attentionReadMarksAtom,
  authUserAtom,
  colorModeAtom,
  currentProjectAtom,
  pushToastAtom,
  type ColorModePreference,
} from '@/store';

const DRAWER_WIDTH = 232;
const HEADER_HEIGHT = 60;
const PAGE_TOP = 32;
const PAGE_BOTTOM = 40;

/** The part of the viewport a page cannot have: the top bar and the page's own padding. */
export const PAGE_INSET = HEADER_HEIGHT + PAGE_TOP + PAGE_BOTTOM;

export interface NavEntry {
  to: string;
  label: string;
  icon: SvgIconComponent;
  /** Rendered only when a project is selected — most screens are project-scoped. */
  needsProject?: boolean;
  adminOnly?: boolean;
  /** Only a superadmin sees it: the Phrase sync writes into every project at once (ADR 0008). */
  superadminOnly?: boolean;
  /** Greyed out with this tooltip: the feature is switched off (`lib/features.ts`). */
  offReason?: string;
}

interface NavSection {
  /** Omitted for the first group: a heading above "Dashboard" would say nothing. */
  title?: string;
  /** The page-guide anchor of the group (`helpAnchor`, so the guide checker sees the literal). */
  help: string;
  entries: NavEntry[];
}

/** The entries of a navigation group this user may open; the API refuses the rest anyway. */
export function visibleEntries(
  entries: NavEntry[],
  viewer: { isAdmin: boolean; isSuperadmin: boolean },
): NavEntry[] {
  return entries.filter(
    (entry) => (!entry.adminOnly || viewer.isAdmin) && (!entry.superadminOnly || viewer.isSuperadmin),
  );
}

export const NAV: NavSection[] = [
  {
    help: helpAnchor('shell-nav-main'),
    entries: [
      { to: '/', label: 'Dashboard', icon: SpaceDashboardOutlinedIcon },
      { to: '/projects', label: 'Projects', icon: FolderOutlinedIcon },
      { to: '/packages', label: 'Packages', icon: Inventory2OutlinedIcon },
      { to: '/glossary', label: 'Glossary', icon: MenuBookOutlinedIcon },
    ],
  },
  {
    title: 'Project',
    help: helpAnchor('shell-nav-project'),
    entries: [
      { to: '/keys', label: 'Keys', icon: KeyOutlinedIcon, needsProject: true },
      { to: '/linked-keys', label: 'Linked keys', icon: LinkOutlinedIcon, needsProject: true },
      { to: '/releases', label: 'Releases', icon: RocketLaunchOutlinedIcon, needsProject: true },
      { to: '/imports', label: 'Imports', icon: FileUploadOutlinedIcon, needsProject: true },
      { to: '/missing', label: 'Missing keys', icon: PlaylistAddOutlinedIcon, needsProject: true },
      { to: '/suggestions', label: 'Suggestions', icon: RateReviewOutlinedIcon, needsProject: true },
      {
        to: '/ai-jobs',
        label: 'AI jobs',
        icon: AutoAwesomeOutlinedIcon,
        needsProject: true,
        offReason: AI_ENABLED ? undefined : AI_DISABLED_REASON,
      },
      { to: '/activity', label: 'Activity', icon: HistoryOutlinedIcon, needsProject: true },
    ],
  },
  {
    title: 'Administration',
    help: helpAnchor('shell-nav-admin'),
    entries: [
      { to: '/settings/users', label: 'Users', icon: PeopleAltOutlinedIcon, adminOnly: true },
      { to: '/settings/api-tokens', label: 'API tokens', icon: VpnKeyOutlinedIcon, adminOnly: true },
      { to: '/settings/locales', label: 'Locales', icon: TranslateOutlinedIcon, adminOnly: true },
      {
        to: '/settings/publish-targets',
        label: 'Publish targets',
        icon: CloudUploadOutlinedIcon,
        adminOnly: true,
      },
      { to: '/settings/phrase-sync', label: 'Phrase sync', icon: SyncOutlinedIcon, superadminOnly: true },
    ],
  },
];

/**
 * What is waiting for the signed-in user, in the top bar (spec §9.2).
 *
 * Deliberately not a live feed: it lists the three things somebody has to act on — an undecided
 * package suggestion, a failed release, a reported missing key — and links straight to the screen
 * where each is handled. A notification nobody can act on is noise.
 *
 * Marking an item read only quiets the badge; the item stays in the list, dimmed, until the work
 * is done, and turns unread again when its count grows (a new failure, another reported key).
 */
function NotificationsBell() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { items, isLoaded } = useAttention();
  const [marks, setMarks] = useAtom(attentionReadMarksAtom);

  useEffect(() => {
    if (!isLoaded) return;
    const pruned = pruneAttentionReadMarks(marks, items);
    if (pruned) setMarks(pruned);
  }, [isLoaded, items, marks, setMarks]);

  const unread = items.filter((item) => isAttentionUnread(item, marks));
  const markRead = (read: AttentionItem[]) =>
    setMarks((current) => ({
      ...current,
      ...Object.fromEntries(read.map((item) => [attentionId(item), item.count])),
    }));

  return (
    <>
      <Tooltip
        title={
          unread.length
            ? `${unread.length} new thing${unread.length === 1 ? '' : 's'} need attention`
            : items.length
              ? 'Nothing new'
              : 'Nothing waiting'
        }
      >
        <IconButton
          onClick={(event) => setAnchor(event.currentTarget)}
          data-help="shell-notifications"
          aria-label="Notifications"
          sx={topBarSquare}
        >
          {/* Orange: something is waiting for you to act — the one meaning orange has. */}
          <Badge badgeContent={unread.length} color="warning" max={99}>
            <NotificationsNoneOutlinedIcon sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, width: 400 } } }}
      >
        {items.length === 0 ? (
          <MenuItem disabled sx={{ opacity: 1 }}>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Nothing is waiting for you.
            </Typography>
          </MenuItem>
        ) : (
          [
            <Box
              key="header"
              sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, pt: 0.5, pb: 1 }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
                {unread.length ? `${unread.length} new` : 'All read'}
              </Typography>
              <Button
                size="small"
                startIcon={<DoneAllOutlinedIcon fontSize="small" />}
                disabled={!unread.length}
                onClick={() => markRead(unread)}
              >
                Mark all as read
              </Button>
            </Box>,
            <Divider key="divider" />,
            ...items.map((item) => {
              const isNew = isAttentionUnread(item, marks);
              return (
                <MenuItem
                  key={attentionId(item)}
                  onClick={() => {
                    setAnchor(null);
                    // Opening the screen where the work is done is reading it.
                    markRead([item]);
                    void navigate(item.to);
                  }}
                  sx={{ gap: 1.5, alignItems: 'flex-start', py: 1.25, whiteSpace: 'normal' }}
                >
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 8,
                      height: 8,
                      mt: 0.75,
                      flex: 'none',
                      borderRadius: '50%',
                      bgcolor: isNew ? 'warning.main' : 'divider',
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: isNew ? 500 : 400,
                        color: isNew ? 'text.primary' : 'text.secondary',
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {item.detail}
                    </Typography>
                  </Box>
                  {isNew && (
                    <Tooltip title="Mark as read">
                      <IconButton
                        size="small"
                        aria-label={`Mark ${item.label} as read`}
                        onClick={(event) => {
                          event.stopPropagation();
                          markRead([item]);
                        }}
                      >
                        <DoneOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </MenuItem>
              );
            }),
          ]
        )}
      </Menu>
    </>
  );
}

/** The 38 px bordered square the top bar's icon buttons share. */
const topBarSquare = {
  width: 38,
  height: 38,
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: 'text.primary',
} as const;

/**
 * The application frame (spec §9.2): navigation, project switcher, key search, bell, page guide,
 * user menu.
 *
 * The project switcher writes `currentProjectAtom`, and the project-scoped routes read the code
 * from the URL — the URL stays the source of truth so a link into the editor always works.
 */
export function Shell({
  children,
  onSignOut,
}: {
  children: ReactNode;
  onSignOut: () => void;
}) {
  const me = useAtomValue(authUserAtom);
  const isAdmin = Boolean(me?.isAdmin ?? me?.isSuperadmin);
  const isSuperadmin = Boolean(me?.isSuperadmin);
  const { pathname } = useLocation();
  const pushToast = useSetAtom(pushToastAtom);
  const wide = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'), { noSsr: true });
  // On a narrow screen the navigation is a drawer, open only on the page it was opened on: following
  // a link changes the path, and that closes it.
  const [navOpenOn, setNavOpenOn] = useState<string | null>(null);
  const navOpen = navOpenOn === pathname;
  // Memoised: the launcher filters the guide's steps by this object, and a fresh one per render
  // would redo that on every shell re-render.
  const helpViewer = useMemo(() => ({ isAdmin }), [isAdmin]);


  const navigation = (
    <Navigation isAdmin={isAdmin} isSuperadmin={isSuperadmin} onSignOut={onSignOut} />
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {wide ? (
        <Box
          component="nav"
          aria-label="Main"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            // Pinned beside the page: only the content scrolls, the navigation stays where it is.
            position: 'sticky',
            top: 0,
            height: '100vh',
          }}
        >
          {navigation}
        </Box>
      ) : (
        <Drawer
          open={navOpen}
          onClose={() => setNavOpenOn(null)}
          slotProps={{ paper: { sx: { width: DRAWER_WIDTH }, component: 'nav' } }}
        >
          {navigation}
        </Drawer>
      )}

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          component="header"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            height: HEADER_HEIGHT,
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: { xs: 2, md: 4 },
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {wide ? null : (
            <IconButton aria-label="Open navigation" onClick={() => setNavOpenOn(pathname)} sx={topBarSquare}>
              <MenuOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
          <ProjectSwitcher />
          <Box sx={{ flex: 1 }} />
          <GlobalKeySearch />
          <NotificationsBell />
          <HelpLauncher pathname={pathname} viewer={helpViewer} notify={pushToast} />
        </Box>

        <Box
          component="main"
          // Re-mounted per route, so every screen settles in instead of snapping in place.
          key={pathname}
          sx={{
            flex: 1,
            minWidth: 0,
            px: { xs: 2, md: 5 },
            pt: `${PAGE_TOP}px`,
            pb: `${PAGE_BOTTOM}px`,
            maxWidth: 1680,
            width: '100%',
            '@keyframes gls-settle': {
              from: { opacity: 0, transform: 'translateY(8px)' },
              to: { opacity: 1, transform: 'none' },
            },
            animation: `gls-settle ${MOTION.expressive}ms ${MOTION.settle} both`,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

/** Brand, the three navigation groups, and the signed-in user pinned to the bottom. */
function Navigation({
  isAdmin,
  isSuperadmin,
  onSignOut,
}: {
  isAdmin: boolean;
  isSuperadmin: boolean;
  onSignOut: () => void;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', px: 1.75, pt: 2.5, pb: 1.5 }}>
      <Box
        component={Link}
        to="/"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1,
          pb: 2.25,
          textDecoration: 'none',
        }}
      >
        <Box component="img" src="/glossa.svg" alt="" sx={{ width: 44, height: 44 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
          <Typography
            component="span"
            sx={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}
          >
            GrECo
          </Typography>
          <Typography
            component="span"
            sx={{ fontSize: 17, fontWeight: 600, letterSpacing: '0.02em', color: 'spectrum.mark' }}
          >
            Glossa
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', mx: -0.5, px: 0.5 }}>
        {NAV.map((section) => {
          const entries = visibleEntries(section.entries, { isAdmin, isSuperadmin });
          if (!entries.length) return null;
          return (
            <Box key={section.title ?? 'main'} data-help={section.help}>
              {section.title ? (
                <Typography
                  sx={{
                    fontSize: 11,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    px: 1.5,
                    pt: 2.5,
                    pb: 0.75,
                  }}
                >
                  {section.title}
                </Typography>
              ) : null}
              <List disablePadding>
                {entries.map((entry) => (
                  <NavEntryLink key={entry.to} entry={entry} />
                ))}
              </List>
            </Box>
          );
        })}
      </Box>

      <UserMenu onSignOut={onSignOut} />
    </Box>
  );
}

function NavEntryLink({ entry }: { entry: NavEntry }) {
  const project = useAtomValue(currentProjectAtom);
  const { items } = useAttention();
  const to = entry.needsProject && project ? `/projects/${project}${entry.to}` : entry.to;
  const disabled = Boolean((entry.needsProject && !project) || entry.offReason);
  // The bell's own items, shown where the work is done: a failed release beside Releases,
  // reported keys beside Missing keys, open suggestions beside Suggestions.
  const waiting = entry.needsProject && project
    ? items.find((item) => item.to === to)?.count
    : undefined;
  const Icon = entry.icon;

  const link = (
    <ListItemButton
      component={NavLink}
      to={to}
      disabled={disabled}
      // Exact for Projects too: every project-scoped screen lives under /projects/… and has its
      // own entry below, which is the one that should light up.
      end={entry.to === '/' || entry.to === '/projects'}
      sx={{
        gap: 1.5,
        px: 1.5,
        py: 1,
        mb: 0.25,
        color: 'text.primary',
        transition: (theme) =>
          theme.transitions.create(['background-color', 'color'], {
            duration: MOTION.standard,
            easing: MOTION.move,
          }),
        '&.active': {
          bgcolor: 'spectrum.tint',
          color: 'primary.main',
          fontWeight: 600,
          '&:hover': { bgcolor: 'spectrum.tint' },
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
        <Icon sx={{ fontSize: 19 }} />
      </ListItemIcon>
      <ListItemText
        primary={entry.label}
        sx={{ my: 0 }}
        slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 'inherit' } } }}
      />
      {waiting ? (
        <Box
          component="span"
          aria-label={`${waiting} waiting`}
          sx={{
            fontSize: 11,
            fontWeight: 600,
            lineHeight: '18px',
            px: 1,
            borderRadius: 999,
            bgcolor: 'spectrum.act.tint',
            color: 'spectrum.act.ink',
          }}
        >
          {waiting > 99 ? '99+' : waiting}
        </Box>
      ) : null}
    </ListItemButton>
  );
  // A disabled item fires no pointer events, so the tooltip hangs on a wrapper around it.
  return entry.offReason ? (
    <Tooltip title={entry.offReason} placement="right">
      <span>{link}</span>
    </Tooltip>
  ) : (
    link
  );
}

const MODE_OPTIONS: { value: ColorModePreference; label: string; icon: SvgIconComponent }[] = [
  { value: 'system', label: 'System', icon: SettingsBrightnessOutlinedIcon },
  { value: 'light', label: 'Light', icon: LightModeOutlinedIcon },
  { value: 'dark', label: 'Dark', icon: DarkModeOutlinedIcon },
];

const initialsOf = (text: string) =>
  text
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

/** The signed-in user at the foot of the navigation: account details, appearance, sign-out. */
function UserMenu({ onSignOut }: { onSignOut: () => void }) {
  const me = useAtomValue(authUserAtom);
  const [mode, setMode] = useAtom(colorModeAtom);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const name = me?.name ?? me?.email ?? 'Signed in';

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(event) => setAnchor(event.currentTarget)}
        data-help="shell-user-menu"
        aria-haspopup="menu"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          width: '100%',
          mt: 1.5,
          px: 1,
          py: 1.25,
          border: 0,
          borderTop: 1,
          borderColor: 'divider',
          background: 'none',
          color: 'text.primary',
          font: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
          borderRadius: 0,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            width: 34,
            height: 34,
            flex: 'none',
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            fontSize: 12,
            fontWeight: 600,
            bgcolor: 'spectrum.tint',
            color: 'primary.main',
          }}
        >
          {initialsOf(name) || '?'}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: 13, fontWeight: 500 }}>
            {name}
          </Typography>
          <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>
            Account · theme
          </Typography>
        </Box>
        <UnfoldMoreOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 260, mt: -1 } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }} noWrap>
            {me?.email ?? '—'}
          </Typography>
          {me?.roles?.length ? (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{me.roles.join(', ')}</Typography>
          ) : null}
        </Box>
        <Divider />
        <Box sx={{ px: 2, pt: 1.25, pb: 1.5 }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.75 }}>Appearance</Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={mode}
            onChange={(_, next: ColorModePreference | null) => next && setMode(next)}
            aria-label="Appearance"
          >
            {MODE_OPTIONS.map(({ value, label, icon: Icon }) => (
              <ToggleButton key={value} value={value} sx={{ gap: 0.5, fontSize: 12, py: 0.5 }}>
                <Icon sx={{ fontSize: 16 }} />
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onSignOut();
          }}
        >
          <ListItemIcon>
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}

/** Switches the project the scoped screens work on (spec §9.2). */
function ProjectSwitcher() {
  const me = useAtomValue(authUserAtom);
  const [current, setCurrent] = useAtom(currentProjectAtom);
  const params = useParams();
  const navigate = useNavigate();

  const projects = me?.projects ?? [];
  const active = params.code ?? current ?? '';
  const value = projects.find((project) => project.code === active) ?? null;

  if (!projects.length) return null;

  // Searchable: there are dozens of projects, too many to scroll through in a plain select.
  // `boolean` for DisableClearable keeps `null` (no project chosen yet) a legal value.
  return (
    <Autocomplete<ProjectOption, false, boolean>
      options={projects}
      value={value}
      onChange={(_event, project) => {
        if (!project) return;
        setCurrent(project.code);
        void navigate(`/projects/${project.code}/keys`);
      }}
      disableClearable
      autoHighlight
      getOptionLabel={(project) => `${project.code} · ${project.name}`}
      getOptionKey={(project) => project.id}
      isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
      filterOptions={filterProjects}
      noOptionsText="No project matches"
      data-help="shell-project-switcher"
      // As tall as the other top-bar controls (38 px): a small Autocomplete pads its root 6px on
      // top of the input's own padding.
      sx={{
        width: { xs: 180, md: 300 },
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': { py: '3.5px', fontWeight: 500 },
      }}
      slotProps={{
        popper: { placement: 'bottom-start', style: { width: 340 } },
        listbox: { sx: { maxHeight: 480 } },
      }}
      renderOption={(props, project) => {
        const { key, ...rest } = props;
        return (
          <Box component="li" key={key} {...rest} sx={{ gap: 1.5 }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 13, minWidth: 40, color: 'primary.main' }}>
              {project.code}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }} noWrap>
              {project.name}
            </Typography>
          </Box>
        );
      }}
      renderInput={(inputProps) => <TextField {...inputProps} placeholder="Select a project" />}
    />
  );
}

type ProjectOption = Me['projects'][number];

/** Typing finds a project by its code or by its name. */
const filterProjects = createFilterOptions<ProjectOption>({
  stringify: (project) => `${project.code} ${project.name}`,
});
